import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { 
  Package, Lock, CheckCircle, CreditCard, Mail, User,
  Download, ArrowRight, FileText, AlertCircle
} from 'lucide-react';
import axios from 'axios';
import SEO from '../components/shared/SEO';

const API = process.env.REACT_APP_BACKEND_URL;

// Template Packet Data (same as ToolkitPage)
const TEMPLATE_PACKETS = [
  { id: 'cdl-success', title: 'CDL Success Packet', price: 25 },
  { id: 'trucking-operations', title: 'Trucking Operations Packet', price: 25 },
  { id: 'freight-broker', title: 'Freight Broker Packet', price: 25 },
  { id: 'authority-builder', title: 'Authority Builder Packet', price: 25 },
  { id: 'compliance', title: 'Compliance Packet', price: 25 },
  { id: 'dispatch', title: 'Dispatch Packet', price: 25 },
];

const BUNDLE_PRICE = 125;

const ToolkitCheckoutPage = () => {
  const [searchParams] = useSearchParams();
  const [isProcessing, setIsProcessing] = useState(false);
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    cardNumber: '',
    expiry: '',
    cvc: ''
  });

  // Determine what's being purchased
  const purchaseType = searchParams.get('type');
  const selectedPacketIds = searchParams.get('packets')?.split(',').filter(Boolean) || [];
  
  const isBundle = purchaseType === 'bundle' || selectedPacketIds.length === TEMPLATE_PACKETS.length;
  const selectedPackets = isBundle 
    ? TEMPLATE_PACKETS 
    : TEMPLATE_PACKETS.filter(p => selectedPacketIds.includes(p.id));

  const getTotal = () => {
    if (isBundle) return BUNDLE_PRICE;
    return selectedPackets.reduce((sum, p) => sum + p.price, 0);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsProcessing(true);
    setError(null);

    try {
      const response = await axios.post(`${API}/api/toolkit/purchase`, {
        name: formData.name,
        email: formData.email,
        purchase_type: isBundle ? 'bundle' : 'individual',
        packet_ids: selectedPackets.map(p => p.id),
        card_last_four: formData.cardNumber.slice(-4)
      });

      if (response.data.success) {
        setSuccess(response.data);
      } else {
        setError('Purchase failed. Please try again.');
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'An error occurred. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  // Redirect if no packets selected
  if (!isBundle && selectedPackets.length === 0) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="w-16 h-16 text-[#D4AF37] mx-auto mb-4" />
          <h1 className="text-white text-2xl font-bold mb-4">No Packets Selected</h1>
          <p className="text-white/60 mb-6">Please select at least one template packet to purchase.</p>
          <Link to="/toolkit" className="px-6 py-3 bg-[#D4AF37] text-black font-bold">
            Browse Packets
          </Link>
        </div>
      </div>
    );
  }

  // Success State
  if (success) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] py-16" data-testid="toolkit-success">
        <SEO title="Purchase Complete | Queen B Resources" />
        <div className="max-w-2xl mx-auto px-6">
          <div className="text-center mb-8">
            <div className="w-20 h-20 mx-auto bg-green-500/20 rounded-full flex items-center justify-center mb-6">
              <CheckCircle className="w-10 h-10 text-green-500" />
            </div>
            <h1 className="font-['Barlow_Condensed'] text-4xl font-bold text-white uppercase mb-2">
              Purchase Complete!
            </h1>
            <p className="text-white/60">Your templates are ready to download</p>
          </div>

          {/* Order Summary */}
          <div className="bg-[#121212] border border-white/10 p-6 mb-6">
            <h3 className="text-white font-bold mb-4">Order Summary</h3>
            <div className="space-y-3">
              <div className="flex justify-between text-sm">
                <span className="text-white/60">Order ID</span>
                <span className="text-white font-mono">{success.purchase_id}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-white/60">Access Code</span>
                <span className="text-[#D4AF37] font-bold font-mono">{success.access_code}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-white/60">Amount Paid</span>
                <span className="text-[#D4AF37] font-bold">${success.total_amount}</span>
              </div>
              <div className="border-t border-white/10 pt-3">
                <p className="text-white/60 text-sm mb-2">Packets Included:</p>
                {success.packets.map(packetId => {
                  const packet = TEMPLATE_PACKETS.find(p => p.id === packetId);
                  return (
                    <div key={packetId} className="flex items-center gap-2 text-sm text-white/80 py-1">
                      <CheckCircle className="w-4 h-4 text-green-500" />
                      {packet?.title || packetId}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Email Confirmation */}
          <div className="bg-green-500/10 border border-green-500/30 p-4 mb-6 flex items-center gap-3">
            <Mail className="w-6 h-6 text-green-500 flex-shrink-0" />
            <div>
              <p className="text-green-400 font-bold text-sm">Confirmation Email Sent!</p>
              <p className="text-white/60 text-xs">Download links sent to {formData.email}</p>
            </div>
          </div>

          {/* Download Section */}
          <div className="bg-[#D4AF37]/10 border border-[#D4AF37]/30 p-6 mb-6">
            <div className="flex items-center gap-2 mb-4">
              <Download className="w-5 h-5 text-[#D4AF37]" />
              <h3 className="text-white font-bold">Download Your Templates</h3>
            </div>
            <div className="space-y-2">
              {success.download_links?.map((link, idx) => (
                <a
                  key={idx}
                  href={link.url}
                  download
                  className="flex items-center justify-between p-3 bg-black/30 hover:bg-black/50 transition-colors"
                >
                  <div className="flex items-center gap-2">
                    <FileText className="w-4 h-4 text-[#D4AF37]" />
                    <span className="text-white text-sm">{link.name}</span>
                  </div>
                  <Download className="w-4 h-4 text-white/50" />
                </a>
              ))}
            </div>
          </div>

          {/* Access Info */}
          <div className="bg-[#121212] border border-white/10 p-4 mb-8">
            <p className="text-white/70 text-sm">
              <strong className="text-white">Hive Access:</strong> You can also access your templates anytime 
              through The Hive dashboard using your access code: <span className="text-[#D4AF37] font-mono">{success.access_code}</span>
            </p>
          </div>

          {/* CTAs */}
          <div className="flex flex-col sm:flex-row gap-4">
            <Link
              to="/hive/login"
              className="flex-1 py-3 bg-[#D4AF37] text-black font-bold uppercase tracking-wider text-center hover:bg-[#B8962E] transition-colors"
            >
              Go to The Hive
            </Link>
            <Link
              to="/toolkit"
              className="flex-1 py-3 bg-white/10 text-white font-bold uppercase tracking-wider text-center hover:bg-white/20 transition-colors"
            >
              Back to Resources
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A] py-16" data-testid="toolkit-checkout">
      <SEO title="Checkout | Queen B Resources" />
      <div className="max-w-4xl mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#D4AF37]/10 border border-[#D4AF37]/30 mb-4">
            <Package className="w-4 h-4 text-[#D4AF37]" />
            <span className="text-[#D4AF37] text-sm font-bold uppercase tracking-widest">Checkout</span>
          </div>
          <h1 className="font-['Barlow_Condensed'] text-4xl font-bold text-white uppercase">
            Complete Your Purchase
          </h1>
        </div>

        <div className="grid md:grid-cols-2 gap-8">
          {/* Order Summary */}
          <div className="bg-[#121212] border border-white/10 p-6 h-fit">
            <h2 className="text-white font-bold text-lg mb-4">Order Summary</h2>
            
            {isBundle && (
              <div className="bg-[#D4AF37]/10 border border-[#D4AF37]/30 p-3 mb-4">
                <p className="text-[#D4AF37] font-bold text-sm">Complete Bundle - All 6 Packets</p>
              </div>
            )}

            <div className="space-y-3 mb-6">
              {selectedPackets.map(packet => (
                <div key={packet.id} className="flex items-center justify-between py-2 border-b border-white/5">
                  <div className="flex items-center gap-2">
                    <FileText className="w-4 h-4 text-[#D4AF37]" />
                    <span className="text-white/80 text-sm">{packet.title}</span>
                  </div>
                  <span className="text-white/50 text-sm">${packet.price}</span>
                </div>
              ))}
            </div>

            {isBundle && (
              <div className="flex justify-between text-sm text-white/50 mb-2">
                <span>Bundle Discount</span>
                <span className="text-green-400">-$25</span>
              </div>
            )}

            <div className="flex justify-between pt-4 border-t border-white/10">
              <span className="text-white font-bold">Total</span>
              <span className="text-[#D4AF37] text-2xl font-bold">${getTotal()}</span>
            </div>
          </div>

          {/* Payment Form */}
          <div className="bg-[#121212] border border-white/10 p-6">
            <h2 className="text-white font-bold text-lg mb-4">Payment Details</h2>
            
            {error && (
              <div className="bg-red-500/10 border border-red-500/30 p-3 mb-4 flex items-center gap-2">
                <AlertCircle className="w-4 h-4 text-red-400" />
                <span className="text-red-400 text-sm">{error}</span>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="text-white/60 text-sm block mb-1">Full Name</label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    required
                    className="w-full bg-black border border-white/10 px-10 py-3 text-white focus:border-[#D4AF37] outline-none"
                    placeholder="Enter your name"
                  />
                </div>
              </div>

              <div>
                <label className="text-white/60 text-sm block mb-1">Email Address</label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    required
                    className="w-full bg-black border border-white/10 px-10 py-3 text-white focus:border-[#D4AF37] outline-none"
                    placeholder="Enter your email"
                  />
                </div>
              </div>

              <div>
                <label className="text-white/60 text-sm block mb-1">Card Number</label>
                <div className="relative">
                  <CreditCard className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
                  <input
                    type="text"
                    name="cardNumber"
                    value={formData.cardNumber}
                    onChange={(e) => setFormData({...formData, cardNumber: e.target.value.replace(/\D/g, '').slice(0, 16)})}
                    required
                    className="w-full bg-black border border-white/10 px-10 py-3 text-white focus:border-[#D4AF37] outline-none"
                    placeholder="1234 5678 9012 3456"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-white/60 text-sm block mb-1">Expiry</label>
                  <input
                    type="text"
                    name="expiry"
                    value={formData.expiry}
                    onChange={(e) => {
                      let value = e.target.value.replace(/\D/g, '').slice(0, 4);
                      if (value.length >= 2) value = value.slice(0, 2) + '/' + value.slice(2);
                      setFormData({...formData, expiry: value});
                    }}
                    required
                    className="w-full bg-black border border-white/10 px-4 py-3 text-white focus:border-[#D4AF37] outline-none"
                    placeholder="MM/YY"
                  />
                </div>
                <div>
                  <label className="text-white/60 text-sm block mb-1">CVC</label>
                  <input
                    type="text"
                    name="cvc"
                    value={formData.cvc}
                    onChange={(e) => setFormData({...formData, cvc: e.target.value.replace(/\D/g, '').slice(0, 4)})}
                    required
                    className="w-full bg-black border border-white/10 px-4 py-3 text-white focus:border-[#D4AF37] outline-none"
                    placeholder="123"
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={isProcessing}
                className="w-full py-4 bg-[#D4AF37] text-black font-bold uppercase tracking-wider hover:bg-[#B8962E] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 mt-6"
                data-testid="submit-purchase"
              >
                {isProcessing ? (
                  <>
                    <div className="w-5 h-5 border-2 border-black/30 border-t-black rounded-full animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    <Lock className="w-5 h-5" />
                    Complete Purchase - ${getTotal()}
                  </>
                )}
              </button>

              {/* Non-Refundable Notice */}
              <div className="bg-red-500/10 border border-red-500/30 p-3">
                <p className="text-red-400 text-xs text-center font-bold">
                  ALL SALES ARE FINAL. Digital products are non-refundable.
                </p>
              </div>

              <p className="text-white/40 text-xs text-center">
                By completing this purchase, you agree to our{' '}
                <Link to="/refund-policy" className="text-[#D4AF37] hover:underline">Refund Policy</Link>
                {' '}and Terms of Service.
              </p>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ToolkitCheckoutPage;
